import React, { useState,useEffect } from "react";
import { toast } from 'react-toastify';
import {format, parseISO } from 'date-fns';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { getDefaultReviewList,getSingleReviewTemplateDetail,searchAmazonReview } from "../../../../utils/reviewApi";
import { getSingleWidgetDetail } from "../../../../staticWidgets/utils/CommonWidgetApi";
import RatingStars from "../../../../components/RatingStars";
import ReviewPopup from "./ReviewPopup";
import BadgeWidget from "./BadgeWidget";
import FloatingWidget from "./FloatingWidget";

const CommonWidget = ({selectedLayout,isTemplateId, widgetId,reviewTabData}) => {
    const [apiData, setApiData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedReview, setSelectedReview] = useState(null);
    const [visibleGridCount, setVisibleGridCount] = useState(8);
    const [visibleListLayoutCount, setVisibleListLayoutCount] = useState(5);
    const [visibleMasonryLayoutCount, setVisibleMasonryLayoutCount] = useState(8);

    // Default values for the reviewTabData
    const {
        is_website_frontend,
        layout_is_full_width,
        business_name,
        layout_custom_width,
        style_background_color,
        header_is_widget_title,
        header_title,
        header_caption,
        setting_rating_format,
        style_widget_title_color,
        style_widget_title_fonts_title_size,
        style_widget_title_caption_color,
        style_widget_title_fonts_caption_size,
        style_header_background_color,
        header_is_show_header,
        header_write_reviews_button,
        header_source_icon,
        header_rating,
        header_number_of_reviews,
        style_header_text_color,
        style_header_write_review_background_color,
        style_header_write_review_text_color,
        header_write_review_button_type,
        style_rating_stars_color,
        layout_rows_on_mobile,
        layout_rows,
        style_load_more_button_color,
        style_load_more_button_text_color
    } = reviewTabData || {};

    // Map layout name to a specific class
    const layoutClassMap = {
        carousel_layout: 'upgadeify-carousel-style',
        list_layout: 'upgadeify-list-style',
        grid_layout: 'upgadeify-grid-style',
        masonry_layout: 'upgadeify-masonry-style',
        slider_layout: 'upgadeify-slider-style',
    };
    const layoutClass = layoutClassMap[selectedLayout] || '';

    //Helper to get slidesToShow from width
    const getSlidesToShow = (width) => {
        const w = Number(width);
        if (w >= 1024) return 3;
        if (w >= 768) return 2;
        return 1;
    };
    //State use for handle responsive logic
    const [slidesToShow, setSlidesToShow] = useState(getSlidesToShow(layout_custom_width));

    //Update slidesToShow when window resizes or width changes
    useEffect(() => {
        const handleResize = () => {
        setSlidesToShow(getSlidesToShow(layout_custom_width));
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [layout_custom_width]);

    //Carousel Slider Settings
    const carouselSliderSettings = {
        dots: false,
        infinite: true,
        speed: 500,
        slidesToShow: slidesToShow,
        slidesToScroll: 1,
        arrows: true,
        autoplay: false,
        responsive: [
        {
            breakpoint: 768,
            settings: {
                slidesToShow: 1,
                slidesToScroll: 1,
                arrows: true,
                dots: false,
            },
        },
        ],
    };

    //Slider Settings
    const sliderSettings = {
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: slidesToShow,
        slidesToScroll: 3,
        arrows: false,
        autoplay: false,
        responsive: [
            {
                breakpoint: 768,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    arrows: true,
                    dots: false,
                },
            },
        ],
    };

    // Fetching Review data
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                let result;
                if (isTemplateId) {
                    result = await getSingleReviewTemplateDetail(isTemplateId);
                } else if (widgetId) {
                    result = await getSingleWidgetDetail(widgetId);
                } else if (business_name) {
                    const payload = { business_name: business_name };
                    result = await searchAmazonReview(payload);
                } else {
                    let app_type = 'Amazon_Review';
                    result = await getDefaultReviewList(app_type);
                }
                setApiData(result); 
            } catch (error) {
                setApiData(null); 
                toast.warning(error.message);
            } finally {
                setLoading(false); 
            }
        };

        fetchData();
    }, [isTemplateId, widgetId, business_name]);

    //Handle Layout Rows and Columns
    const layoutColumns = 3;
    // Simple: detect if the screen is mobile once at render time
    const isMobile = window.innerWidth <= 768;
    const layoutRows = isMobile
        ? layout_rows_on_mobile
        : layout_rows;

    useEffect(() => {
        const newGridCount = layoutRows * layoutColumns;
        const newMasonryCount = layoutRows * layoutColumns;

        setVisibleGridCount(newGridCount);
        setVisibleListLayoutCount(layoutRows);
        setVisibleMasonryLayoutCount(newMasonryCount);
    }, [layoutRows, layoutColumns]);

    // Open popup when clicking "Read More"
    const handleReadMoreClick = (review) => {
        setSelectedReview(review);
    };

    // Close the popup
    const handleClosePopup = () => {
        setSelectedReview(null);
    };

    // Load More List Layout function
    const handleGridLoadMore = () => {
        setVisibleGridCount((prevCount) => prevCount + 8);
    };
    
    // Load List Layout More function
    const handleListLayoutLoadMore = () => {
        setVisibleListLayoutCount((prevCount) => prevCount + 5);
    };

    // Load Masonry Layout More function
    const handleMasonryLayoutLoadMore = () => {
        setVisibleMasonryLayoutCount((prevCount) => prevCount + 8);
    };

    //Handle dynamic styles based on layout
    const dynamicStyle =
        selectedLayout !== 'badge_layout' && selectedLayout !== 'floating_badge_layout'
            ? Number(layout_is_full_width) === 1
            ? {
                backgroundColor: style_background_color,
                margin: '0 auto',
            }
            : {
                backgroundColor: style_background_color,
                maxWidth: `${layout_custom_width}px`,
                margin: '0 auto',
                }
            : {};

    // Get grid layout style based on custom width
    const getGridLayoutStyle = () => {
        if (selectedLayout !== 'grid_layout') return {};

        if (layout_custom_width > 1280 && layout_custom_width < 1921) {
            return {
                gridTemplateColumns: 'repeat(auto-fill, minmax(24%, 1fr))',
            
            };
        }

        if (layout_custom_width > 991 && layout_custom_width < 1280) {
            return {
                gridTemplateColumns: 'repeat(auto-fill, minmax(32%, 1fr))',
        
            };
        }

        if (layout_custom_width > 540 && layout_custom_width < 991) {
            return {
                gridTemplateColumns: 'repeat(auto-fill, minmax(48%, 1fr))',
            
            };
        }

        if (layout_custom_width > 0 && layout_custom_width < 540) {
            return {
                gridTemplateColumns: 'repeat(auto-fill, minmax(100%, 1fr))',
            
            };
        }

        // Default fallback
        return {
            gridTemplateColumns: 'repeat(auto-fill, minmax(32%, 1fr))',
        };
    };

    return (
        <div className={`upgadeify-review-template-widget upgadeify ${layoutClass} ${selectedReview ? 'carousel-popup-open' : ''} ${is_website_frontend}`}  style={dynamicStyle}>
            {/* Loader and No Api Data Handling */}
            {loading ? (
                <div className="loader-container">
                    <div className="spinner"></div>
                    <p>Loading please wait...</p>
                </div>
            ) : apiData?.status === true ? (
            <>
            {/* Show Header Section Accordgin to condition*/}
            {selectedLayout !== 'badge_layout' && selectedLayout !== 'floating_badge_layout' && (
                <>
                {Number(header_is_widget_title) === 1 && (
                    <>
                        <div className="upgadeify-amazon-main-title" style={{ color: style_widget_title_color,fontSize: style_widget_title_fonts_title_size }}>{header_title || ""}</div>
                        <div className="upgadeify-amazon-main-caption" style={{ color: style_widget_title_caption_color,fontSize: style_widget_title_fonts_caption_size }}>{header_caption || ""}</div>
                    </>
                )}
                {Number(header_is_show_header) === 1 &&
                    (Number(header_source_icon) === 1 ||
                        Number(header_rating) === 1 ||
                        Number(header_number_of_reviews) === 1 ||
                        Number(header_write_reviews_button) === 1) && (
                        <div className="upgadeify-amazon-reviews-header" style={{ backgroundColor: style_header_background_color,}}>
                    
                            <div className='upgadeify-amazon-reviews-header-left'>
                                {Number(header_source_icon) === 1 && (
                                <div className='upgadeify-amazon-reviews-header-title'>
                                    <svg width="114" height="35" viewBox="0 0 114 35" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M70.3129 26.7099C63.7226 31.5606 54.1799 34.1502 45.9576 34.1502C34.9732 34.2051 24.3639 30.1575 16.2076 22.7999C15.5956 22.2446 16.1509 21.4852 16.8876 21.9159C25.3592 26.8459 35.8312 29.8096 46.6489 29.8096C53.9476 29.8096 61.9716 28.3022 69.3552 25.1686C70.4659 24.6926 71.4009 25.8996 70.3129 26.7099Z" fill="#EC912D"/>
                                        <path d="M73.0433 23.579C73.8933 24.6557 72.1083 29.104 71.315 31.0873C71.077 31.688 71.5927 31.9317 72.1367 31.4783C75.6727 28.5203 76.5793 22.3323 75.8597 21.437C75.14 20.5417 68.9633 19.7823 65.2007 22.4343C64.617 22.8367 64.7133 23.3977 65.3593 23.324C67.4843 23.069 72.2047 22.5023 73.0433 23.579Z" fill="#EC912D"/>
                                        <path d="M65.5067 3.73434V1.156C65.5067 0.764997 65.807 0.504332 66.1583 0.504332H77.69C78.0583 0.504332 78.353 0.770664 78.353 1.156V3.366C78.353 3.73433 78.0413 4.216 77.486 4.981L71.5133 13.5093C73.7347 13.4527 76.075 13.787 78.0867 14.9203C78.54 15.1753 78.6647 15.5493 78.7043 15.9233V18.6717C78.7043 19.0457 78.285 19.482 77.8543 19.2553C75.9696 18.285 73.8797 17.7806 71.7599 17.7846C69.6401 17.7885 67.5521 18.3006 65.671 19.278C65.2743 19.4933 64.855 19.057 64.855 18.683V16.0763C64.855 15.657 64.855 14.943 65.28 14.3027L72.1933 4.386H66.1697C65.8013 4.386 65.501 4.11967 65.501 3.74M23.4543 19.7993H19.941C19.7844 19.7874 19.6367 19.7216 19.5231 19.6132C19.4094 19.5047 19.3368 19.3602 19.3177 19.2043V1.20133C19.3177 0.838666 19.618 0.555331 19.9977 0.555331H23.2673C23.6073 0.566664 23.8793 0.832997 23.902 1.156V3.50766H23.9643C24.8143 1.23533 26.4237 0.175667 28.5827 0.175667C30.7813 0.175667 32.1527 1.23533 33.1387 3.50766C33.4889 2.51052 34.147 1.65044 35.0178 1.05156C35.8886 0.452679 36.9272 0.14596 37.9837 0.175667C38.7669 0.161717 39.5423 0.332473 40.2473 0.674098C40.9522 1.01572 41.5667 1.51861 42.041 2.142C43.1517 3.655 42.925 5.85933 42.925 7.786V19.1477C42.9235 19.2352 42.9047 19.3216 42.8697 19.4019C42.8346 19.4821 42.784 19.5546 42.7208 19.6152C42.6576 19.6758 42.583 19.7233 42.5013 19.7549C42.4197 19.7865 42.3325 19.8016 42.245 19.7993H38.743C38.5754 19.7883 38.4178 19.7156 38.3007 19.5952C38.1835 19.4749 38.115 19.3155 38.1083 19.1477V9.605C38.1083 8.84566 38.1763 6.953 38.012 6.23334C37.7513 5.02634 36.9637 4.68633 35.9493 4.68633C35.0993 4.68633 34.2153 5.253 33.8527 6.15967C33.49 7.06633 33.5297 8.585 33.5297 9.605V19.1477C33.5282 19.2352 33.5094 19.3216 33.4744 19.4019C33.4393 19.4821 33.3887 19.5546 33.3255 19.6152C33.2623 19.6758 33.1877 19.7233 33.106 19.7549C33.0243 19.7865 32.9372 19.8016 32.8497 19.7993H29.3533C29.1854 19.7907 29.027 19.7186 28.9102 19.5976C28.7934 19.4766 28.727 19.3158 28.7243 19.1477L28.7187 9.605C28.7187 7.599 29.0473 4.64666 26.5597 4.64666C24.038 4.64666 24.1343 7.52533 24.1343 9.605V19.1477C24.1329 19.2352 24.1141 19.3216 24.079 19.4019C24.044 19.4821 23.9934 19.5546 23.9302 19.6152C23.8669 19.6758 23.7923 19.7233 23.7107 19.7549C23.629 19.7865 23.5419 19.8016 23.4543 19.7993ZM88.2867 0.175667C93.4887 0.175667 96.305 4.64667 96.305 10.3303C96.305 15.8157 93.1883 20.1733 88.281 20.1733C83.1697 20.1733 80.3873 15.7023 80.3873 10.1377C80.3873 4.52766 83.2093 0.175667 88.2867 0.175667ZM88.3037 3.84767C85.7197 3.84767 85.5553 7.37233 85.5553 9.571C85.5553 11.7697 85.5213 16.4673 88.2753 16.4673C90.9953 16.4673 91.12 12.6707 91.12 10.3643C91.12 8.84566 91.0633 7.03233 90.5987 5.593C90.202 4.34066 89.42 3.84767 88.3037 3.84767ZM103.054 19.7993H99.5577C99.3911 19.7869 99.2349 19.7136 99.1189 19.5935C99.0029 19.4733 98.9352 19.3146 98.9287 19.1477L98.923 1.139C98.9428 0.974275 99.0236 0.822946 99.1495 0.714889C99.2754 0.606831 99.4372 0.549896 99.603 0.555331H102.85C102.994 0.562496 103.132 0.615206 103.244 0.705853C103.356 0.796499 103.436 0.920399 103.473 1.05967V3.808H103.541C104.522 1.34867 105.899 0.175667 108.324 0.175667C109.894 0.175667 111.429 0.742332 112.415 2.295C113.333 3.73433 113.333 6.15966 113.333 7.905V19.2327C113.306 19.3927 113.223 19.5379 113.098 19.6418C112.973 19.7457 112.816 19.8016 112.653 19.7993H109.14C108.988 19.7887 108.843 19.727 108.73 19.6241C108.617 19.5213 108.542 19.3834 108.517 19.2327V9.452C108.517 7.48567 108.743 4.607 106.324 4.607C105.474 4.607 104.68 5.17367 104.295 6.04634C103.802 7.14567 103.74 8.23933 103.74 9.452V19.1477C103.732 19.324 103.657 19.4906 103.529 19.6123C103.401 19.7339 103.231 19.801 103.054 19.7993ZM56.321 11.203V10.4437C53.7767 10.4437 51.102 10.982 51.102 13.974C51.102 15.487 51.8897 16.5127 53.2383 16.5127C54.2187 16.5127 55.1083 15.9063 55.6637 14.9203C56.355 13.7077 56.321 12.5687 56.321 11.203ZM59.857 19.7483C59.7437 19.8442 59.6041 19.9036 59.4565 19.9188C59.3088 19.9339 59.1601 19.9041 59.0297 19.8333C57.8623 18.87 57.6527 18.4167 57.0123 17.493C55.0857 19.4593 53.7257 20.043 51.2323 20.043C48.28 20.043 45.985 18.2297 45.985 14.5803C45.985 11.7357 47.5207 9.792 49.725 8.84567C51.629 8.007 54.2867 7.85967 56.321 7.62733V7.174C56.321 6.341 56.3777 5.355 55.8903 4.63533C55.4653 3.98933 54.6437 3.723 53.9183 3.723C52.5867 3.723 51.3967 4.403 51.102 5.82533C51.0453 6.14267 50.813 6.45433 50.49 6.47133L47.09 6.103C46.8067 6.04066 46.4837 5.80833 46.5687 5.36633C47.3507 1.24666 51.0793 0 54.417 0C56.117 0 58.3497 0.453333 59.6983 1.751C61.404 3.33767 61.2397 5.46833 61.2397 7.78033V13.2487C61.2397 14.892 61.9197 15.6117 62.5657 16.5013C62.7867 16.813 62.8377 17.1927 62.5487 17.4307C61.8347 18.0313 60.5653 19.1363 59.8627 19.7597L59.857 19.7483ZM10.336 11.203V10.4437C7.79733 10.4437 5.12267 10.982 5.12267 13.974C5.12267 15.487 5.90467 16.5127 7.25333 16.5127C8.23933 16.5127 9.12333 15.9063 9.67867 14.9203C10.37 13.7077 10.336 12.5687 10.336 11.203ZM13.872 19.7483C13.7587 19.8442 13.6191 19.9036 13.4715 19.9188C13.3238 19.9339 13.1751 19.9041 13.0447 19.8333C11.8773 18.87 11.6733 18.4167 11.033 17.493C9.10633 19.4593 7.74633 20.043 5.253 20.043C2.30067 20.043 0 18.2297 0 14.5803C0 11.7357 1.54133 9.792 3.74 8.84567C5.644 8.007 8.30167 7.85967 10.336 7.62733V7.174C10.336 6.341 10.3983 5.355 9.911 4.63533C9.48033 3.98933 8.66433 3.723 7.94467 3.723C6.60733 3.723 5.41167 4.403 5.12267 5.82533C5.066 6.14267 4.828 6.45433 4.51633 6.47133L1.11067 6.103C0.827334 6.04066 0.510001 5.80833 0.589334 5.36633C1.37133 1.24666 5.09433 0 8.432 0C10.1377 0 12.3703 0.453333 13.7133 1.751C15.419 3.33767 15.2603 5.46833 15.2603 7.78033V13.2487C15.2603 14.892 15.9403 15.6117 16.5807 16.5013C16.8073 16.813 16.8583 17.1927 16.5693 17.4307C15.8553 18.0313 14.586 19.1363 13.8833 19.7597L13.872 19.7483Z" fill="black" />
                                    </svg>
                                    <div className='upgadeify-amazon-reviews-header-title-text' style={{color: style_header_text_color,}}>Reviews</div>
                                </div>
                                )}
                                <div className='upgadeify-amazon-reviews-header-rating'>
                                    {Number(header_rating) === 1 && (
                                    <>
                                        <div className="upgadeify-amazon-reviews-header-rating-text" style={{color: style_header_text_color,}}>{setting_rating_format === '4.9' ? Number(apiData.data.average_rating).toFixed(1) : Number(apiData.data.average_rating).toFixed(2) }</div>
                                        <div className="upgadeify-amazon-reviews-item-rating">
                                            <RatingStars average_rating={apiData.data.average_rating} newColor={style_rating_stars_color} />
                                        </div>
                                    </>
                                    )}
                                    {Number(header_number_of_reviews) === 1 && (
                                        <span className="upgadeify-amazon-reviews-header-number-of-reviews-text">({apiData.data.total_reviews})</span>
                                    )}
                                </div>
                            </div>
                            {Number(header_write_reviews_button) === 1 && (
                                <div className='upgadeify-amazon-reviews-header-right'>
                                    <a
                                        href={apiData?.data?.google_url?.startsWith('http') ? apiData.data.google_url : `https://${apiData.data.google_url}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className='upgadeify-amazon-reviews-header-button'
                                        style={{color: style_header_write_review_text_color,backgroundColor: style_header_write_review_background_color,fontSize: header_write_review_button_type}}>Review us on Amazon
                                    </a>
                                </div>
                            )}
                        </div>
                    )}
                </>
                )}
            
                {/*If Carousel Layout Type*/}
                {selectedLayout === 'carousel_layout' && (
                    <Slider {...carouselSliderSettings}>
                        {apiData.data.review_list.map((review) => (
                            <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData}/>
                        ))}
                    </Slider>
                )}
                
                {/*If List Layout Type*/}
                {selectedLayout === 'list_layout' && (
                    <>
                        <div className='upgadeify-list_layout-layout'>
                            {apiData.data.review_list.slice(0, visibleListLayoutCount).map((review) => (
                                <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData}/>
                            ))}
                           
                        </div>
                         {/* Load More Button  */}
                            {visibleListLayoutCount < apiData.data.review_list.length && (
                                <div className="load-more-button">
                                    <button onClick={handleListLayoutLoadMore} style={{backgroundColor: style_load_more_button_color,color: style_load_more_button_text_color}}>Load More</button>
                                </div>
                            )}
                    </>
                )}

                {/*If Grid Layout Type*/}
                {selectedLayout === 'grid_layout' && (
                    <>
                        <div className='upgadeify-grid-layout' style={getGridLayoutStyle()}>
                        {apiData.data.review_list.slice(0, visibleGridCount).map((review) => (
                                <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData}/>
                            ))}
                           
                        </div>
                         {visibleGridCount < apiData.data.review_list.length && (
                                <div className="load-more-button">
                                    <button onClick={handleGridLoadMore} style={{backgroundColor: style_load_more_button_color,color: style_load_more_button_text_color}}>Load More</button>
                                </div>
                            )}
                    </>
                )}

                {/*If Masonry Layout Type*/}
                {selectedLayout === 'masonry_layout' && (
                    <>
                        <div className='upgadeify-masonry_layout-layout'>
                            {apiData.data.review_list.slice(0, visibleMasonryLayoutCount).map((review) => (
                                <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData}/>
                            ))}
                          
                        </div>
                          {/* Load More Button  */}
                            {visibleMasonryLayoutCount < apiData.data.review_list.length && (
                                <div className="load-more-button">
                                    <button onClick={handleMasonryLayoutLoadMore} style={{backgroundColor: style_load_more_button_color,color: style_load_more_button_text_color}}>Load More</button>
                                </div>
                            )}
                    </>
                )}

                {/*If Slider Layout Type*/}
                {selectedLayout === 'slider_layout' && (
                    <Slider {...sliderSettings}>
                        {apiData.data.review_list.map((review) => (
                            <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData} />
                        ))}
                    </Slider>
                )}

                {/*If Badge Layout Type*/}
                {selectedLayout === 'badge_layout' && (
                    <BadgeWidget selectedLayout={selectedLayout} isTemplateId={isTemplateId} widgetId={widgetId} reviewTabData={reviewTabData} />
                )}

                {/*If Floating Badge Layout Type*/}
                {selectedLayout === 'floating_badge_layout' && (
                    <FloatingWidget selectedLayout={selectedLayout} isTemplateId={isTemplateId} widgetId={widgetId} reviewTabData={reviewTabData} />
                )}

                {/*Common Popup */}
                {selectedReview && <ReviewPopup review={selectedReview} onClose={handleClosePopup} />}
            </>
            ) : (
                <div className="no-inner-result-found">
                    <p>No Amazon Product reviews found please check the product ASIN.</p>
                </div>
            )}
        </div>
    );
};

const ReviewLoopItem = ({ review, handleReadMoreClick, reviewTabData }) => (
    <>
        <div className="upgadeify-amazon-reviews">
            <div className='upgadeify-amazon-reviews-item' style={{ padding: `${reviewTabData.layout_item_spacing}px`, backgroundColor: reviewTabData.style_review_background_color,}}>
               <div className='upgadeify-amazon-reviews-head'>
                {Number(reviewTabData.review_is_show_reviewer_picture) === 1 && (
                    <div className="upgadeify-amazon-reviews-item-image">
                        {review.image ? (
                            <img src={review.image} alt="Review" />
                        ) : (
                            <div className="fallback-avatar">
                                {review.name.charAt(0)}
                            </div>
                        )}
                    </div>
                )}
                <div className='upgadeify-amazon-reviews-item-name'>
                    {Number(reviewTabData.review_is_show_reviewer_name) === 1 && (
                        Number(reviewTabData.setting_is_enable_external_links) === 1 && review.author_url ? ( 
                            <a
                                href={review.author_url} 
                                target={Number(reviewTabData.setting_is_open_links_in_new_tab) === 1 ? "_blank" : "_self"}
                                rel={Number(reviewTabData.setting_is_open_links_in_new_tab) === 1 ? "noopener noreferrer" : undefined}
                                style={{ color: reviewTabData.style_review_reviewer_name_color }}
                            >
                                <span className="upgadeify-amazon-reviews-item-name-text" style={{ color: reviewTabData.style_review_reviewer_name_color }}>{review.name}</span>
                            </a>
                        ) : (
                            <span className="upgadeify-amazon-reviews-item-name-text" style={{ color: reviewTabData.style_review_reviewer_name_color }}>
                                {review.name}
                            </span>
                        )
                    )}

                    {Number(reviewTabData.review_is_show_verified_badge) === 1 && (
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14" className="review-verified-badge-icon" aria-hidden="true">
                            <path fill={reviewTabData.style_review_verified_badge_color} d="M6.757.236a.35.35 0 0 1 .486 0l1.106 1.07a.35.35 0 0 0 .329.089l1.493-.375a.35.35 0 0 1 .422.244l.422 1.48a.35.35 0 0 0 .24.24l1.481.423a.35.35 0 0 1 .244.422l-.375 1.493a.35.35 0 0 0 .088.329l1.071 1.106a.35.35 0 0 1 0 .486l-1.07 1.106a.35.35 0 0 0-.089.329l.375 1.493a.35.35 0 0 1-.244.422l-1.48.422a.35.35 0 0 0-.24.24l-.423 1.481a.35.35 0 0 1-.422.244l-1.493-.375a.35.35 0 0 0-.329.088l-1.106 1.071a.35.35 0 0 1-.486 0l-1.106-1.07a.35.35 0 0 0-.329-.089l-1.493.375a.35.35 0 0 1-.422-.244l-.422-1.48a.35.35 0 0 0-.24-.24l-1.481-.423a.35.35 0 0 1-.244-.422l.375-1.493a.35.35 0 0 0-.088-.329L.236 7.243a.35.35 0 0 1 0-.486l1.07-1.106a.35.35 0 0 0 .089-.329L1.02 3.829a.35.35 0 0 1 .244-.422l1.48-.422a.35.35 0 0 0 .24-.24l.423-1.481a.35.35 0 0 1 .422-.244l1.493.375a.35.35 0 0 0 .329-.088L6.757.236Z"></path><path fill="#fff" fill-rule="evenodd" d="M9.065 4.85a.644.644 0 0 1 .899 0 .615.615 0 0 1 .053.823l-.053.059L6.48 9.15a.645.645 0 0 1-.84.052l-.06-.052-1.66-1.527a.616.616 0 0 1 0-.882.645.645 0 0 1 .84-.052l.06.052 1.21 1.086 3.034-2.978Z" clip-rule="evenodd"></path>
                        </svg>
                    )}

                    <span className="upgadeify-amazon-reviews-item-date"> 
                        {Number(reviewTabData.review_is_show_date) === 1 && (
                            <span style={{color: reviewTabData.style_review_date_color,}}>{review.review_date ? format(parseISO(review.review_date), 'yyyy-MM-dd') : ''}</span>
                        )}
                        {Number(reviewTabData.review_is_show_review_source) === 1 && (  
                            <>on <a href={`https://www.amazon.com/gp/customer-reviews/${review.review_id}`} target="blank">Amazon</a></>
                        )}
                    </span>
                </div>
               </div>
                <div className="upgadeify-amazon-reviews-item-content">
                    {Number(reviewTabData.review_is_show_rating) === 1 && (
                        <span className="upgadeify-amazon-reviews-item-rating" style={{ fontSize: reviewTabData.style_review_rating_size }}>
                            <RatingStars average_rating={review.rating} newColor={reviewTabData.style_rating_stars_color}/>
                        </span>
                    )}
                    <div className="upgadeify-amazon-reviews-item-text" style={{color: `${reviewTabData.style_review_text_color}`,fontSize: reviewTabData.style_review_text_font_size}}>
                        {review.description.length > 50
                        ? `${review.description.slice(0, 50)}...`
                        : review.description}
                    </div>
                    {review.description.length > 50 && (
                        <div className='upgadeify-amazon-reviews-readmore' onClick={() => handleReadMoreClick(review)} style={{color: reviewTabData.style_read_more_color,fontSize: reviewTabData.style_review_text_font_size}}>
                                Read more
                        </div>
                   )}
                </div>
            </div>
        </div>
    </>
);

export default CommonWidget;
